'use client';
import dynamic from 'next/dynamic';
import ClientTestimonials from '@/components/home/client-testimonials';
import DedicatedHero from '@/components/dedicated-hero';
import DefaultBreadcrumb from '@/components/common/breadcrumbs';
import DedicatedCard from '@/components/common/dedicated-card';

import MWSVG from '@/components/common/svg';
import MetaTags from '@/components/meta-tag/meta-tag';
import Blog from '@/components/home/blog';

const ContactInfo = dynamic(() => import('@/components/common/contact-info'));
const ContactForm = dynamic(() => import('@/components/common/contact-form'));

const Page = () => {
    return (
        <>
            <MetaTags />
            <DedicatedHero
                className=""
                pageTitle="Time & Material Engagement Model for Agile Software Development"
                isDesc={true}
                desc="Hire a time and material team of skilled professionals to build and continuously improve your vision without limits."
                alt="hero image"
                title="hero image"
                width={459}
                height={528}
                path={'time-material'}
                isButton={true}
            />
            <DefaultBreadcrumb parentPage="Technologies" currentPage="Time & Material" />
            <section className="section section-py">
                <div className="container lg:container mx-auto">
                    <div className="row grid md:grid-cols-12 lg:grid-cols-12">
                        <div className="md:col-span-8">
                            <div className="flex flex-col gap-[30px]">
                                <div>
                                    <p className="fs-md mb-2.5">
                                        Are you looking for a software development approach that is flexible and adaptable,
                                        perfectly suited to meeting your project&apos;s changing needs? The time and material
                                        engagement model could be the most suitable solution for you.
                                    </p>
                                    <p className="fs-md mb-0">
                                        The time and material hiring model offers flexibility in adjusting project requirements
                                        and scope throughout the development process. This model is ideal for projects that lack a
                                        complete upfront definition or are expected to undergo changes during the development
                                        journey.
                                    </p>
                                </div>
                                <div>
                                    <h2 className="fs-xl mb-2.5">What is the Time & Material Engagement Model?</h2>
                                    <p className="fs-md mb-0">
                                        The time and material engagement model is a software development approach that offers
                                        flexibility in project scope and requirements. Instead of a fixed price, your invoice will
                                        be based on the actual time spent by our development team. Additionally, you will be
                                        charged for the materials used during the project.
                                    </p>
                                </div>
                                <div>
                                    <h2 className="fs-xl mb-2.5">How does the Time & Material Hiring Model Work?</h2>
                                    <p className="fs-md mb-2.5">
                                        The time and material engagement model operates on a transparent and agile framework:
                                    </p>
                                    <ul className="list-decimal text-secondary fs-md mb-0">
                                        <li className="mb-1">
                                            You discuss your project vision and initial requirements with the development team.
                                            The team will provide an estimate of the time and materials needed to complete the
                                            project.
                                        </li>
                                        <li className="mb-1">
                                            As the project continues, you can make adjustments or include new features as per
                                            evolving business requirements or market trends. The development team will adapt
                                            accordingly to incorporate these changes.
                                        </li>
                                        <li className="mb-1">
                                            In this phase, the billing process is determined by the amount of time each team
                                            member invests and the resources utilized during the development process.
                                        </li>
                                        <li className="mb-0">
                                            We will keep you updated on the project&apos;s progress and maintain communication
                                            with you so that you are well-informed and can provide feedback at every stage of the
                                            development process.
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h2 className="fs-xl mb-2.5">
                                        What are the Benefits of the Time & Material Engagement Model?
                                    </h2>
                                    <p className="fs-md mb-2.5">
                                        Opting for the time-and-material engagement model offers several advantages:
                                    </p>
                                    <ul className="list-none text-secondary fs-md mb-0 dedicated-content ps-0">
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Flexibility:</span>
                                            You have the liberty to adjust project requirements and priorities as needed, ensuring
                                            that the final product precisely aligns with your vision.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Agile Development:</span>
                                            This model embraces an agile approach, allowing for faster iterations and quick
                                            responses to changing market demands.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor"> Transparency:</span>
                                            You gain complete visibility into the development process, including resource
                                            allocation and costs, promoting a transparent working relationship.
                                        </li>
                                        <li className="mb2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Cost-Efficiency:</span>
                                            For projects with evolving requirements, the time and material model can often prove
                                            more cost-effective than a fixed-cost approach.
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <h2 className="fs-xl mb-2.5">Why Choose Moweb for Time & Material Software Development?</h2>
                                    <p className="fs-md mb-2.5">
                                        Here at Moweb, we specialize in delivering exceptional time and material software
                                        development services. Here are the reasons why you should choose us:
                                    </p>
                                    <ul className="list-none text-secondary fs-md mb-0 dedicated-content ps-0">
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Agile Methodology Experts:</span>
                                            Our development team is skilled in agile methodologies, ensuring efficient project
                                            management and iterative development.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Customer-Centric Approach:</span>
                                            Our main focus is your satisfaction, and we work together to provide a solution that
                                            surpasses your expectations.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor"> Transparent Communication:</span>· You will
                                            receive frequent updates and have open communication channels to keep you informed
                                            about the project&apos;s progress. This will also make it easy to provide feedback.
                                        </li>
                                        <li className="mb2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            <span className="fw-600 text-textcolor">Versatile Team:</span>
                                            Our team consists of a wide range of developers, designers, and domain specialists who
                                            can meet diverse project requirements.
                                        </li>
                                    </ul>
                                </div>

                                <div>
                                    <h2 className="fs-xl mb-2.5">Who is the Time & Material Engagement Model Suitable for?</h2>
                                    <p className="fs-md mb-2.5">
                                        The time and material engagement model is ideal for businesses that meet the following
                                        criteria:
                                    </p>

                                    <ul className="list-none fw-400 text-secondary fs-md mb-0 dedicated-content ps-0">
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Have projects with requirements that may change or are uncertain.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Need the flexibility to adjust during the development process.
                                        </li>
                                        <li className="mb-2.5 relative ms-2">
                                            <MWSVG id="tabler-icon-point-filled" svgClassName="min-w-[24px]" />
                                            Desire an agile and transparent software development approach.
                                        </li>
                                    </ul>
                                    <p className="fs-md mb-0">
                                        You can choose the time and material engagement model if you need a software development
                                        model that adapts to your changing needs while still maintaining budget control. We can
                                        collaborate on your software project with precision and agility to bring your ideas to
                                        life.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="md:col-span-4">
                            <DedicatedCard />
                        </div>
                    </div>
                </div>
            </section>
            <section className="">
                <ClientTestimonials />
            </section>
            <Blog />
            <div className="section bg-primary py-100 dark:text-light dark:bg-black">
                <div className="container lg:container md:mx-auto">
                    <div className="row grid md:grid-cols-12 lg:grid-cols-12">
                        <ContactForm />
                        <ContactInfo />
                    </div>
                </div>
            </div>
        </>
    );
};

export default Page;
